import React, { useEffect, useState } from 'react'
import { Button, Form, Input, Row, Col } from 'antd'
import { CaretLeft } from '@phosphor-icons/react'
import ButtonSpinner from 'components/global/ButtonSpinner'
import { infoMessage } from 'api/global'
import EventManager from '../GeneralDetails/InputComps/EventManager'
import { useGetOrgOrganizers } from 'api/Organizations'
import { OrgOrganizerParams } from 'types/organization'
import { classesEventStore } from 'state/classes/classesStore'
import { useEditClassStep2Mutation } from 'api/Classes/Classes'
import { UpdateEduClassStep2Body } from 'types/Classes/Classes'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { validatePhone } from 'helper/utility'

interface ContactProps {
  handleNext: (data: any) => void
  handleBack: () => void
}

const Contact: React.FC<ContactProps> = ({ handleNext, handleBack }) => {
  const [form] = Form.useForm()
  const { classesData } = classesEventStore(store => ({
    classesData: store.classesData,
  }))

  const [selectedOrganizer, setSelectedOrganizer] = useState<string[]>([])
  const [phone, setPhone] = useState<any>({
    dialCode: classesData?.phone?.dialCode || '',
    isoCode: classesData?.phone?.isoCode || '',
    number: classesData?.phone?.number || '',
  })
  const [defaultOrganizer, setDefaultOrganizer] = useState(null)

  const orgId = classesData?.organizationId || classesData?.organization?._id;
  const { mutate: editClassStep2, isLoading: editLoading } = useEditClassStep2Mutation()

  const { data: orgOrganizers, isLoading: orgOrganizersLoading, refetch: orgOrganizersRefetch } = useGetOrgOrganizers(
    { status: 'approved' },
    orgId
  )

  // Sync initial values
  useEffect(() => {
    if (classesData) {
      form.setFieldsValue({
        website: classesData.website,
        email: classesData.email,
        phone: classesData.phone?.number,
      })
      setSelectedOrganizer(classesData.administratorIds || [])
    }
  }, [classesData])

  // Set default organizer logic matching your League step
  useEffect(() => {
    if (!orgOrganizersLoading && orgOrganizers) {
      const owner = orgOrganizers?.find(item => item.accessLevel === 'owner')
      if (owner) {
        setDefaultOrganizer(owner)
        if (selectedOrganizer.length === 0) {
          setSelectedOrganizer([owner.organizerId])
        }
      }
    }
  }, [orgOrganizersLoading, orgOrganizers])

  useEffect(() => {
    if (orgId) orgOrganizersRefetch();
  }, [orgId]);

  const onFinish = async (values: any) => {
    if (selectedOrganizer.length === 0) {
      infoMessage('Event manager is required')
      return
    }

    const payload: UpdateEduClassStep2Body = {
      website: values.website,
      email: values.email,
      phone: phone,
      administratorIds: selectedOrganizer,
    }

    editClassStep2(
      { body: payload, classId: classesData?._id },
      {
        onSuccess: () => {
          handleNext(payload)
        },
      }
    )
  }

  console.log("classes data",classesData)
  return (
    <div className="step2-main-container">
      <h3 className="text-24 w-600">Step 2. Contacts</h3>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        className="mt-20px"
      >
        {/* Website Field */}
        <Row>
          <Col span={24}>
            <label className="input-label required-input">Website</label>
            <Form.Item
              name="website"
              className="form-control"
              rules={[
                { required: true, message: 'Website url is required' },
                { type: 'url', message: 'Please enter a valid URL' },
              ]}
            >
              <Input placeholder="Enter Website URL" className="form-control-field" />
            </Form.Item>
          </Col>
        </Row>

        {/* Email Field */}
        <Row>
          <Col span={24}>
            <label className="input-label required-input">Email</label>
            <Form.Item
              name="email"
              className="form-control"
              rules={[
                { required: true, message: 'Email is required' },
                { type: 'email', message: 'Enter a valid email' },
              ]}
            >
              <Input placeholder="example@gmail.com" className="form-control-field" />
            </Form.Item>
          </Col>
        </Row>

        {/* Phone Field */}
        <Row>
          <Col span={24}>
            <label className="input-label required-input">Phone number</label>
            <Form.Item
              name="phone"
              className="phone-number-form"
              rules={[
                { required: true, message: 'Phone is required' },
                { validator: validatePhone }
              ]}
            >
              <PhoneInput
                country={phone?.isoCode?.toLowerCase() || 'us'}
                containerClass="w-full"
                inputClass="w-full form-control-field"
                onChange={(value, data: any) => {
                  setPhone({
                    dialCode: '+' + data?.dialCode,
                    number: '+' + value,
                    isoCode: data.countryCode.toUpperCase(),
                  })
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Event Manager */}
        {orgOrganizers?.length > 0 && (
          <div className="mt-20px">
            <EventManager
              selectedOrganizer={selectedOrganizer}
              setSelectedOrganizer={setSelectedOrganizer}
              orgOrganizers={orgOrganizers}
              defaultOrganizer={defaultOrganizer}
            />
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="mt-30px event-button-cont">
          <h5 className="flex gap-10px align-center pointer" onClick={handleBack}>
            <CaretLeft size={20} color="#000" />
            <span className="text-15 w-500">Back</span>
          </h5>
          <Button
            className="stepper-button"
            htmlType="submit"
            loading={editLoading}
          >
            {editLoading ? (
              <ButtonSpinner size={20} color="#000" />
            ) : (
              'Next'
            )}
          </Button>
        </div>
      </Form>
    </div>
  )
}

export default Contact